import { ActionReducerMapBuilder, CaseReducer, PayloadAction } from "@reduxjs/toolkit";
import type { AdminState } from "../AdminDb";
import {
  SettlementRequest,
  SettlementRequestStatus,
} from "../../../interfaces/OnboardingObject";
import { ADMIN_ACCESS_DENIED } from "../../../services/thunks/AdminBusinessesThunk";
import {
  fetchAdminSettlementRequests,
  settleAdminSettlementRequest,
} from "../../../services/thunks/AdminSettlementThunk";
import { AdminTabState, createAdminTabState, registerAsync } from "./adminTabState";

// Admin -> Settlements. State shape, initial state, reducer cases and selectors
// for this section; AdminDb only assembles them.
//
// Rows carry payees' payout details (UPI / bank / phone): personal data, held
// in memory only — the store isn't persisted and resetAllState wipes it on
// logout. Never log these rows or copy them into browser storage.

interface StatusBucket {
  rows: SettlementRequest[];
  /** null = never loaded (as opposed to loaded and empty). */
  lastFetchedAt: number | null;
}

export interface AdminSettlementsState extends AdminTabState {
  /** One cache per status, fetched server-side per chip. */
  byStatus: Record<SettlementRequestStatus, StatusBucket>;
  activeStatus: SettlementRequestStatus;
  /** Server said "not yours to see" — a state, not an error to retry. */
  accessDenied: boolean;
}

const emptyBucket = (): StatusBucket => ({ rows: [], lastFetchedAt: null });

export const settlementsInitialState = (): AdminSettlementsState => ({
  ...createAdminTabState(),
  byStatus: { PENDING: emptyBucket(), SETTLED: emptyBucket(), CANCELLED: emptyBucket() },
  activeStatus: "PENDING",
  accessDenied: false,
});

// ── Loading/error map keys (shared with the UI, never hand-typed there) ──────

export const settlementsFetchKey = (status: SettlementRequestStatus): string =>
  `fetchSettlementRequests_${status}`;

export const settleRequestKey = (requestId: string): string => `settle_${requestId}`;

// ── Plain reducers ──────────────────────────────────────────────────────────

const setSettlementStatus: CaseReducer<AdminState, PayloadAction<SettlementRequestStatus>> = (
  state,
  action
) => {
  state.settlements.activeStatus = action.payload;
};

export const settlementsReducers = { setSettlementStatus };

// ── Async cases ─────────────────────────────────────────────────────────────

export function attachSettlementsCases(builder: ActionReducerMapBuilder<AdminState>): void {
  registerAsync(builder, fetchAdminSettlementRequests, {
    tab: (state) => state.settlements,
    key: (status) => settlementsFetchKey(status),
    loading: "Loading settlement requests...",
    onFulfilled: (state, payload) => {
      state.settlements.byStatus[payload.status] = {
        rows: payload.rows,
        lastFetchedAt: Date.now(),
      };
      state.settlements.accessDenied = false;
    },
    onRejected: (state, message) => {
      if (message === ADMIN_ACCESS_DENIED) {
        state.settlements.accessDenied = true;
        return true;
      }
      return false;
    },
  });

  // Settle has a structured rejection (SettleFailure), so it is wired by hand
  // rather than through registerAsync (which expects a string).
  builder
    .addCase(settleAdminSettlementRequest.pending, (state, action) => {
      const key = settleRequestKey(action.meta.arg.request.requestId);
      state.settlements.loadingMap[key] = "Recording payment...";
      state.settlements.errorMap[key] = null;
      state.settlements.successMap[key] = false;
    })
    .addCase(settleAdminSettlementRequest.fulfilled, (state, action) => {
      const requestId = action.meta.arg.request.requestId;
      const key = settleRequestKey(requestId);
      state.settlements.loadingMap[key] = null;
      state.settlements.successMap[key] = true;
      const pending = state.settlements.byStatus.PENDING;
      pending.rows = pending.rows.filter((row) => row.requestId !== requestId);
      // The settled list now has a new row we don't hold: refetch on next open.
      state.settlements.byStatus.SETTLED.lastFetchedAt = null;
    })
    .addCase(settleAdminSettlementRequest.rejected, (state, action) => {
      const key = settleRequestKey(action.meta.arg.request.requestId);
      state.settlements.loadingMap[key] = null;
      state.settlements.errorMap[key] = action.payload?.message ?? "Couldn't record the payment.";
    });
}

// ── Selectors (`any` state to avoid the UiDb import cycle; see approvals.ts) ──

export const selectAdminSettlements = (state: any): AdminSettlementsState =>
  state.admin.settlements;

export const selectAdminSettlementBucket = (
  state: any,
  status: SettlementRequestStatus
): StatusBucket => state.admin.settlements.byStatus[status];
